/**
 * Insights hooks (v1.1-C5) — full real-data binding for the
 * /insights subpages. Replaces every MOCK_* read.
 *
 * R3-P0 fix 2026-05-13 — every hook now accepts the full FilterBar
 * params (range + department + agentId + modelId) and includes them
 * in the query key so the cache invalidates when filters change, and
 * threads them through to the backend as querystring params.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ── R3-P0 filter wiring (defect #1) ──────────────────────────────────────

/** Subset of InsightsFilters shipped over the wire. */
export interface InsightsFilterParams {
  range?: '24h' | '7d' | '30d' | '90d';
  department?: string;       // 'all' or department name
  agentId?: string;          // 'all' or canonical agent slug
  modelId?: string;          // 'all' or canonical model slug
}

/**
 * Build a querystring fragment for the FilterBar filters. Emits only
 * non-'all' filters so the URL stays clean and the route can fall back
 * to its default range when omitted.
 *
 * Exported so the unit tests can assert on the wire-format shape.
 */
export function buildFilterQs(p: InsightsFilterParams | undefined): string {
  if (!p) return '';
  const parts: string[] = [];
  if (p.range) parts.push(`range=${encodeURIComponent(p.range)}`);
  if (p.department && p.department !== 'all') {
    parts.push(`department=${encodeURIComponent(p.department)}`);
  }
  if (p.agentId && p.agentId !== 'all') {
    parts.push(`agentId=${encodeURIComponent(p.agentId)}`);
  }
  if (p.modelId && p.modelId !== 'all') {
    parts.push(`modelId=${encodeURIComponent(p.modelId)}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Stable cache-key suffix from filters. Same shape as buildFilterQs so
 * react-query refetches whenever ANY filter changes.
 */
export function filterKey(p: InsightsFilterParams | undefined): unknown[] {
  return [
    p?.range ?? null,
    p?.department ?? 'all',
    p?.agentId ?? 'all',
    p?.modelId ?? 'all',
  ];
}

export interface InsightsSummary {
  totalQueries: number;
  totalTokens: number;
  avgLatencyMs: number;
  // Bug #1118 — outlier-robust latency percentiles. The Cost tab leads
  // with p50/p95 because the unweighted mean (avgLatencyMs) is dragged
  // into the hundreds of seconds by a handful of hung/wedged queries.
  p50LatencyMs: number;
  p95LatencyMs: number;
  p99LatencyMs: number;
  totalCostDollars: number;
  uniqueUsers30d: number;
  uniqueAgents30d: number;
}

export interface SeriesPoint {
  date: string;
  value: number;
}

export interface AgentRow {
  agentId: string | null;
  queryCount: number;
  totalTokens: number;
}

export interface ModelRow {
  model: string;
  queryCount: number;
  totalTokens: number;
  avgLatencyMs: number;
  totalCostDollars: number;
}

export interface QualitySummary {
  successRate: number;
  totalQueries30d: number;
  failedQueries30d: number;
  avgRagSources: number;
}

export interface KnowledgeSummary {
  collections: number;
  documents: number;
  queriesWithRag30d: number;
  queriesWithoutRag30d: number;
}

export function useInsightsSummary(filters?: InsightsFilterParams) {
  return useQuery<InsightsSummary, Error>({
    queryKey: ['insights-module', 'summary', ...filterKey(filters)],
    queryFn: () =>
      apiClient.get<InsightsSummary>(
        `/insights-module/summary${buildFilterQs(filters)}`,
      ),
  });
}

export function useInsightsTimeSeries(
  metric:
    | 'queries'
    | 'tokens'
    | 'latency'
    | 'cost'
    | 'dau'
    // Bug #1118 — per-day latency percentiles for the Cost-tab chart.
    | 'latency_p50'
    | 'latency_p95'
    | 'latency_p99',
  days = 30,
  filters?: InsightsFilterParams,
) {
  // For time-series the URL also has `metric` + `days`. Build them
  // separately and merge with the filter querystring (which already
  // emits its own `?`).
  const baseParams = `metric=${metric}&days=${days}`;
  const filterQs = buildFilterQs(filters);
  // buildFilterQs returns either '' or '?key=val&...'. Strip the
  // leading '?' so we can join with '&'.
  const filterPairs = filterQs ? filterQs.slice(1) : '';
  const qs = filterPairs ? `?${baseParams}&${filterPairs}` : `?${baseParams}`;
  return useQuery<{ series: SeriesPoint[] }, Error, SeriesPoint[]>({
    queryKey: ['insights-module', 'time-series', metric, days, ...filterKey(filters)],
    queryFn: () =>
      apiClient.get<{ series: SeriesPoint[] }>(
        `/insights-module/time-series${qs}`,
      ),
    select: (r) => r.series,
  });
}

export function useTopAgents(filters?: InsightsFilterParams) {
  return useQuery<{ agents: AgentRow[] }, Error, AgentRow[]>({
    queryKey: ['insights-module', 'top-agents', ...filterKey(filters)],
    queryFn: () =>
      apiClient.get<{ agents: AgentRow[] }>(
        `/insights-module/top-agents${buildFilterQs(filters)}`,
      ),
    select: (r) => r.agents,
  });
}

export function useTopModels(filters?: InsightsFilterParams) {
  return useQuery<{ models: ModelRow[] }, Error, ModelRow[]>({
    queryKey: ['insights-module', 'top-models', ...filterKey(filters)],
    queryFn: () =>
      apiClient.get<{ models: ModelRow[] }>(
        `/insights-module/top-models${buildFilterQs(filters)}`,
      ),
    select: (r) => r.models,
  });
}

export function useInsightsQuality(filters?: InsightsFilterParams) {
  return useQuery<QualitySummary, Error>({
    queryKey: ['insights-module', 'quality', ...filterKey(filters)],
    queryFn: () =>
      apiClient.get<QualitySummary>(
        `/insights-module/quality${buildFilterQs(filters)}`,
      ),
  });
}

export function useInsightsKnowledge(filters?: InsightsFilterParams) {
  return useQuery<KnowledgeSummary, Error>({
    queryKey: ['insights-module', 'knowledge', ...filterKey(filters)],
    queryFn: () =>
      apiClient.get<KnowledgeSummary>(
        `/insights-module/knowledge${buildFilterQs(filters)}`,
      ),
  });
}

// ── R3 P1 — /value + /risk hooks (PR #430 backend had no consumer) ────

export type InsightsRange = '24h' | '7d' | '30d' | '90d';

export interface ValueUseCase {
  agentId: string | null;
  queryCount: number;
  tasksCompleted: number;
  hoursSaved: number;
}

export interface ValueSummary {
  range: string;
  tasksAutomated: number;
  queriesHandled: number;
  hoursSaved: number;
  topUseCases: ValueUseCase[];
  completionRate: number;
}

export interface RiskTrendPoint {
  date: string;
  incidents: number;
  blocked: number;
}

export interface RiskSummary {
  range: string;
  totalIncidents: number;
  openIncidents: number;
  bySeverity: { severity: string; count: number }[];
  byType: { incidentType: string; count: number }[];
  blockedActions: number;
  failedAuditEvents: number;
  trend: RiskTrendPoint[];
}

export function useInsightsValue(
  range: InsightsRange = '30d',
  filters?: Omit<InsightsFilterParams, 'range'>,
) {
  const merged: InsightsFilterParams = { range, ...(filters ?? {}) };
  return useQuery<{ data: ValueSummary }, Error, ValueSummary>({
    queryKey: ['insights-module', 'value', ...filterKey(merged)],
    queryFn: () =>
      apiClient.get<{ data: ValueSummary }>(
        `/insights-module/value${buildFilterQs(merged)}`,
      ),
    select: (r) => r.data,
  });
}

export function useInsightsRisk(
  range: InsightsRange = '30d',
  filters?: Omit<InsightsFilterParams, 'range'>,
) {
  const merged: InsightsFilterParams = { range, ...(filters ?? {}) };
  return useQuery<{ data: RiskSummary }, Error, RiskSummary>({
    queryKey: ['insights-module', 'risk', ...filterKey(merged)],
    queryFn: () =>
      apiClient.get<{ data: RiskSummary }>(
        `/insights-module/risk${buildFilterQs(merged)}`,
      ),
    select: (r) => r.data,
  });
}

// ── /evaluation/runs (RAGAS faithfulness + relevancy) ─────────────────

export interface EvaluationRun {
  id: string;
  datasetId: string;
  status: string;
  totalQuestions: number;
  completedQuestions: number;
  avgFaithfulness: number | null;
  avgRelevancy: number | null;
  createdAt: string;
}

export function useEvaluationRuns() {
  return useQuery<{ data: EvaluationRun[] }, Error, EvaluationRun[]>({
    queryKey: ['evaluation', 'runs'],
    queryFn: () => apiClient.get<{ data: EvaluationRun[] }>('/evaluation/runs'),
    select: (r) => r.data,
  });
}
